/**
 * Exact arithmetic for the pricing engine. Appendix D `MR-01`, `MR-03`, `MR-04`; Appendix V.3.
 *
 * ---
 *
 * **Why this exists at all.**
 *
 * Two rules in Appendix D are individually easy and together are the whole problem:
 *
 * - `MR-01`: money is integer minor units, never a float.
 * - `MR-04`: "Rounding happens at steps 8, 11 and 12 only. **Never mid-calculation.**"
 *
 * Appendix V.3's step 11 is `MR-03`'s step 8, so the pricing engine rounds exactly once, at the very
 * end. Everything before it, steps 5 to 10, must be exact. And those steps multiply by percentages:
 * `amount_percent` is `numeric(6,3)`, and a `percent_of_running` rule applied twice compounds.
 *
 * An integer cannot hold `12345 * 12.345%`. A float can hold it approximately, which is the thing
 * `MR-01` forbids and the thing that makes a pricing error silent and compounding, which is what
 * Appendix V calls this component's defining risk. So the running total is a **rational**: two
 * `bigint`s, exact under addition and multiplication, rounded once when the engine is finished.
 *
 * There is deliberately **no `round()` on `Money`**, for the reason `money.ts` gives: a helper there
 * would be used mid-calculation within a week. Rounding lives here, on a type that only the pricing
 * chain uses, and is called from one place.
 */
export class ExactError extends Error {
}
function gcd(a, b) {
    let x = a < 0n ? -a : a;
    let y = b < 0n ? -b : b;
    while (y !== 0n) {
        const t = y;
        y = x % y;
        x = t;
    }
    return x;
}
export function exact(n, d = 1n) {
    if (d === 0n)
        throw new ExactError('An exact value cannot have a zero denominator');
    // The sign belongs in the numerator, so two values that are equal compare equal.
    const sign = d < 0n ? -1n : 1n;
    const nn = n * sign;
    const dd = d * sign;
    const g = gcd(nn, dd);
    return g === 0n ? { n: 0n, d: 1n } : { n: nn / g, d: dd / g };
}
export const EXACT_ZERO = { n: 0n, d: 1n };
export function fromMinor(minor) {
    return { n: minor, d: 1n };
}
export function addExact(a, b) {
    return exact(a.n * b.d + b.n * a.d, a.d * b.d);
}
export function subtractExact(a, b) {
    return exact(a.n * b.d - b.n * a.d, a.d * b.d);
}
export function multiplyExact(a, b) {
    return exact(a.n * b.n, a.d * b.d);
}
export function compareExact(a, b) {
    const left = a.n * b.d;
    const right = b.n * a.d;
    return left < right ? -1 : left > right ? 1 : 0;
}
export function isNegative(a) {
    return a.n < 0n;
}
/**
 * A percentage, held exactly.
 *
 * `amount_percent` is `numeric(6,3)` in Appendix V.2, so three decimal places. Parsed from the
 * decimal string rather than from a number, because `Number('12.345')` is already the approximation
 * this module exists to avoid, and a price book is read from a database as text.
 */
export function percent(value) {
    const match = /^(-?)(\d+)(?:\.(\d{1,3}))?$/.exec(value.trim());
    if (!match) {
        throw new ExactError(`A percentage must be a decimal with at most 3 places (numeric(6,3)), got ${JSON.stringify(value)}`);
    }
    const sign = match[1] === '-' ? -1n : 1n;
    const whole = BigInt(match[2]);
    const fractionText = (match[3] ?? '').padEnd(3, '0');
    const fraction = BigInt(fractionText);
    // value / 100, exactly: (whole * 1000 + fraction) / (1000 * 100)
    return exact(sign * (whole * 1000n + fraction), 100000n);
}
/**
 * How a fraction becomes a minor unit.
 *
 * **The spec names no rounding mode.** Appendix D fixes rounding at three points and says nothing
 * about direction, and §13's Pakistan pack gives cash denominations but not this. So this is a
 * `RoundingMode` the caller must supply, with no default anywhere in the engine, which follows the
 * pattern `decisions/0004` set for every other threshold the spec names without a value: a shop's
 * country pack chooses it, and until one does, nothing silently picks a side.
 *
 * `half_even` is included because it is what a financial auditor asks for, and `half_up` because it
 * is what a retail counter expects. Both are implemented rather than one being assumed.
 */
export const ROUNDING_MODES = [
    'half_up',
    'half_down',
    'half_even',
    'up',
    'down',
    'toward_zero',
];
export function roundToMinor(value, mode) {
    const { n, d } = value;
    if (d === 1n)
        return n;
    // Truncated quotient and the remainder, both exact. Everything below decides only what to do with
    // the remainder, so no branch can lose a unit.
    const q = n / d;
    const r = n - q * d;
    if (r === 0n)
        return q;
    const negative = r < 0n;
    const twiceRemainder = (negative ? -r : r) * 2n;
    const isHalf = twiceRemainder === d;
    const pastHalf = twiceRemainder > d;
    const awayFromZero = negative ? q - 1n : q + 1n;
    switch (mode) {
        case 'toward_zero':
            return q;
        case 'up':
            // Toward positive infinity.
            return negative ? q : q + 1n;
        case 'down':
            // Toward negative infinity.
            return negative ? q - 1n : q;
        case 'half_up':
            return pastHalf || isHalf ? awayFromZero : q;
        case 'half_down':
            return pastHalf ? awayFromZero : q;
        case 'half_even': {
            if (pastHalf)
                return awayFromZero;
            if (!isHalf)
                return q;
            return q % 2n === 0n ? q : awayFromZero;
        }
        // A mode outside the union can only arrive from a request body cast to `RoundingMode`, and
        // three services do exactly that. Without this the switch returned `undefined` typed as
        // `bigint`, so the bad mode became a `TypeError` several frames later in money arithmetic, or
        // nothing at all where every amount in the sale happened to be whole. `MR-04` says nothing
        // silently picks a side; this is where that becomes impossible rather than discouraged.
        default:
            throw new RangeError(`Unknown rounding mode "${String(mode)}". Appendix D names no default, so one of ` +
                `${ROUNDING_MODES.join(', ')} must be supplied.`);
    }
}
/** For explanations and test failures. Exact, never a float. */
export function formatExact(value, places = 4) {
    const negative = value.n < 0n;
    const n = negative ? -value.n : value.n;
    const scale = 10n ** BigInt(places);
    const scaled = (n * scale) / value.d;
    const whole = scaled / scale;
    const fraction = (scaled % scale).toString().padStart(places, '0').replace(/0+$/, '');
    return `${negative ? '-' : ''}${whole.toString()}${fraction === '' ? '' : `.${fraction}`}`;
}
//# sourceMappingURL=exact.js.map